/**
 * THE-HOME-LOADS-FAST-1 — ONE READ, HOWEVER MANY SURFACES ASK FOR IT.
 *
 * ─── WHAT WAS MEASURED ─────────────────────────────────────────────────────
 *
 * The dashboard home, loaded in a headless browser against the built app with
 * every database answer scripted: **282 reads before the home drew a word.**
 * The menu hop (`useResolvedNav`) sweeps thirty six tables for the counts that
 * decide which menu rows belong on screen, and it is mounted by eight surfaces
 * at once — the route guard, the desktop sidebar, the drawer, the sidebar
 * search, the mobile bar, the panel provider and the home itself. Every one of
 * them ran the whole sweep, and its own `account_navigation` read, separately.
 * Eight identical answers to the same question, in front of somebody waiting.
 *
 * ─── WHAT THIS DOES, AND THE ONE THING IT DELIBERATELY DOES NOT ────────────
 *
 * It shares a read that is STILL IN FLIGHT, and nothing else. The moment it
 * settles the key is dropped, so the next mount reads again exactly as it does
 * today and nothing on screen can be stale by one second more than it already
 * was. A cache with a lifetime would also have collapsed those eight reads, and
 * would have meant a merchant who makes their first job does not see the menu
 * item appear until they reload — which is a behaviour change, and this wave
 * changes no behaviour.
 *
 * NOT REACT QUERY, for the reason `useMenuSignals` and `useAccountNavConfig`
 * both already record: those hooks are rendered by surfaces with no
 * `QueryClient` above them, and a menu that throws for want of a provider is
 * worse than a menu that reads twice. This needs no provider and no context.
 *
 * PURE, AND A LEAF: no imports, so Stryker reaches every branch.
 */

/** The reads that have not answered yet, by key. */
const inFlight = new Map<string, Promise<unknown>>()

/**
 * THE SAME READ, JOINED RATHER THAN REPEATED.
 *
 * The first caller for a key starts the read. Every caller that arrives before
 * it answers gets that same promise. The first caller after it answers starts a
 * fresh one, because the key is gone by then.
 *
 * A read that THROWS is dropped exactly as one that succeeds, so a failure is
 * never remembered and the next surface to ask gets a real attempt.
 */
export function sharedRead<T>(key: string, read: () => Promise<T>): Promise<T> {
  const joined = inFlight.get(key)
  if (joined !== undefined) return joined as Promise<T>
  const started = read()
  inFlight.set(key, started)
  // Dropped by identity: a key that has already been replaced by a later read
  // belongs to that read, and clearing it here would throw away a live answer.
  const forget = () => {
    if (inFlight.get(key) === started) inFlight.delete(key)
  }
  started.then(forget, forget)
  return started
}

/** How many reads are in flight. For the guard, so it can see the sharing. */
export function sharedInFlight(): number {
  return inFlight.size
}

/** Forget everything in flight. For tests, so one does not reach the next. */
export function forgetShared(): void {
  inFlight.clear()
}
